import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  AlertOctagon,
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  CloudCog,
  CloudOff,
  Cpu,
  FilePlus2,
  Gauge,
  Laptop2,
  LifeBuoy,
  Loader2,
  Radio,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  TimerReset,
  UserCheck,
  Wifi,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

import KpiCard from "@/components/common/KpiCard";
import PageHeader from "@/components/common/PageHeader";
import PageSearchInput from "@/components/common/PageSearchInput";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useApp } from "@/context/AppContext";
import {
  createAdminSyncSupportCase,
  getAdminSyncOverview,
  requestAdminSyncCommand,
  updateAdminSyncIncident,
} from "@/lib/api";
import { timeAgo } from "@/lib/formatters";

const HEALTH_FILTERS = [
  ["all", "All health"],
  ["critical", "Critical"],
  ["degraded", "Degraded"],
  ["offline", "Offline"],
  ["watching", "Watching"],
  ["healthy", "Healthy"],
];

const PLATFORM_FILTERS = [
  ["all", "All platforms"],
  ["mobile", "Mobile"],
  ["web", "Web"],
];

function healthTone(status) {
  switch (status) {
    case "critical":
      return "border-rose-200 bg-rose-50 text-rose-800";
    case "degraded":
      return "border-orange-200 bg-orange-50 text-orange-800";
    case "offline":
      return "border-slate-300 bg-slate-100 text-slate-700";
    case "watching":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "healthy":
      return "border-emerald-200 bg-emerald-50 text-emerald-800";
    default:
      return "border-slate-200 bg-slate-50 text-slate-700";
  }
}

function healthDot(status) {
  if (status === "critical") return "bg-rose-500 ring-rose-100";
  if (status === "degraded") return "bg-orange-500 ring-orange-100";
  if (status === "offline") return "bg-slate-400 ring-slate-100";
  if (status === "watching") return "bg-amber-400 ring-amber-100";
  return "bg-emerald-500 ring-emerald-100";
}

function formatDuration(seconds = 0) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.ceil(seconds / 3600)}h`;
  return `${Math.ceil(seconds / 86400)}d`;
}

function queueAge(client) {
  if (!client?.oldestQueuedAt) return "Queue clear";
  return `${formatDuration(Math.max(0, Math.floor((Date.now() - new Date(client.oldestQueuedAt).getTime()) / 1000)))} oldest`;
}

export default function SyncHealth() {
  const queryClient = useQueryClient();
  const { currentAdmin } = useApp();
  const [health, setHealth] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [activeTab, setActiveTab] = useState("command");
  const debouncedSearch = search.trim();

  const filters = useMemo(() => ({
    health: health === "all" ? "" : health,
    limit: 80,
    platform: platform === "all" ? "" : platform,
    query: debouncedSearch,
  }), [debouncedSearch, health, platform]);

  const overview = useQuery({
    queryKey: ["admin-sync-overview", filters],
    queryFn: ({ signal }) => getAdminSyncOverview(filters, signal),
    refetchInterval: 20_000,
  });
  const clients = useMemo(() => overview.data?.clients || [], [overview.data?.clients]);
  const commands = useMemo(() => overview.data?.commands || [], [overview.data?.commands]);
  const incidents = useMemo(() => overview.data?.incidents || [], [overview.data?.incidents]);
  const metrics = overview.data?.metrics || {};
  const selected = clients.find((client) => client.id === selectedId) || clients[0] || null;
  const incident = selected
    ? incidents.find((item) => item.clientId === selected.id && item.status !== "resolved")
    : null;
  const canOperateRemotely = currentAdmin?.permissions?.includes("sync.manage");
  const openCaseDesk = (clientId) => {
    setSelectedId(clientId);
    setActiveTab("case");
  };

  useEffect(() => {
    if (!selectedId && clients.length) setSelectedId(clients[0].id);
    if (selectedId && clients.length && !clients.some((client) => client.id === selectedId)) {
      setSelectedId(clients[0].id);
    }
  }, [clients, selectedId]);

  return (
    <div data-testid="sync-page">
      <PageHeader
        eyebrow="Reliability operations"
        title="Sync Operations Center"
        subtitle="Privacy-safe, installation-level visibility across mobile and web offline queues."
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 rounded-2xl border border-[#0E5B52]/10 bg-white p-1.5 shadow-sm">
          <WorkspaceTab value="command" icon={Gauge} label="Command Center" meta={`${metrics.activeIncidents || 0} active`} />
          <WorkspaceTab value="installations" icon={Cpu} label="Installations" meta={`${metrics.reportingClients || 0} reporting`} />
          <WorkspaceTab value="case" icon={LifeBuoy} label="Case Desk" meta={selected ? selected.shopName : "Select a client"} />
        </TabsList>

        <TabsContent value="command" className="mt-5 space-y-5">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-5">
            <KpiCard label="Healthy shops" value={metrics.healthyShops || 0} icon={Activity} accent="#A7F3D0" hint="All reporting clients healthy" />
            <KpiCard label="Degraded shops" value={metrics.degradedShops || 0} icon={AlertOctagon} accent="#FED7AA" hint="Needs operator attention" />
            <KpiCard label="Failed operations" value={metrics.failedOperations || 0} icon={CloudOff} accent="#FECACA" />
            <KpiCard label="Queued operations" value={metrics.queuedOperations || 0} icon={CloudCog} accent="#BAE6FD" />
            <div className="col-span-2 rounded-xl border border-[#0E5B52]/10 bg-[linear-gradient(135deg,#0E5B52,#08766a)] p-4 text-white shadow-sm xl:col-span-1">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-white/65"><Radio className="h-3.5 w-3.5 text-emerald-300" /> Live pulse</div>
              <div className="mt-3 flex items-end justify-between"><div><div className="text-2xl font-semibold">{metrics.activeIncidents || 0}</div><div className="text-xs text-white/70">active incidents</div></div><div className="text-right text-[10px] text-white/60">{metrics.reportingClients || 0} clients reporting</div></div>
            </div>
          </div>

          <div className="grid gap-4 2xl:grid-cols-[minmax(0,1.6fr)_minmax(330px,.7fr)]">
            <QueueTrend data={overview.data?.trend || []} loading={overview.isLoading} />
            <ReliabilityBrief metrics={metrics} />
          </div>

          <IncidentRail
            incidents={incidents}
            loading={overview.isLoading}
            onOpen={(item) => openCaseDesk(item.clientId)}
            onViewAll={() => setActiveTab("installations")}
          />
        </TabsContent>

        <TabsContent value="installations" className="mt-5">
          <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[#0E5B52]/8 bg-white p-3 shadow-sm lg:flex-row lg:items-center">
            <PageSearchInput className="flex-1" testId="sync-search" value={search} onSearch={setSearch} placeholder="Search shop, branch or installation…" inputClassName="rounded-lg border-[#0E5B52]/10" />
            <Select value={health} onValueChange={setHealth}>
              <SelectTrigger className="h-10 w-full rounded-lg lg:w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{HEALTH_FILTERS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="h-10 w-full rounded-lg lg:w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{PLATFORM_FILTERS.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={() => overview.refetch()} disabled={overview.isFetching}><RefreshCw className={`h-4 w-4 ${overview.isFetching ? "animate-spin" : ""}`} /></Button>
          </div>

          <section className="overflow-hidden rounded-2xl border border-[#0E5B52]/10 bg-white shadow-[0_22px_60px_-40px_rgba(6,78,59,.4)]">
            <div className="flex items-center justify-between border-b border-[#0E5B52]/8 bg-[#F7FAF8] px-4 py-3">
              <div><div className="text-xs font-semibold text-[#064E3B]">Installation directory</div><div className="text-[10px] text-[#6B7280]">{clients.length} visible clients · select one to open its Case Desk</div></div>
              {overview.isFetching && <Loader2 className="h-4 w-4 animate-spin text-[#0E5B52]" />}
            </div>
            {overview.isLoading ? <ClientListSkeleton /> : overview.isError ? (
              <EmptyState title="Operations feed unavailable" body="The health service could not be reached." action={() => overview.refetch()} />
            ) : clients.length ? (
              <div className="grid gap-3 p-3 md:grid-cols-2 2xl:grid-cols-3">
                {clients.map((client) => (
                  <InstallationCard
                    client={client}
                    incident={incidents.find((item) => item.clientId === client.id && item.status !== "resolved")}
                    key={client.id}
                    onClick={() => openCaseDesk(client.id)}
                  />
                ))}
              </div>
            ) : <EmptyState title="No reporting installations" body="Clients appear here after their first privacy-safe heartbeat." />}
          </section>
        </TabsContent>

        <TabsContent value="case" className="mt-5">
          <div className="mb-4 flex flex-col gap-3 rounded-xl border border-[#0E5B52]/8 bg-white p-3 shadow-sm sm:flex-row sm:items-center">
            <div className="flex-1"><div className="text-xs font-semibold text-[#064E3B]">Case Desk</div><div className="mt-0.5 text-[10px] text-[#6B7280]">One installation at a time, with diagnostics and remediation in context.</div></div>
            <Select value={selected?.id || ""} onValueChange={setSelectedId} disabled={!clients.length}>
              <SelectTrigger className="h-10 w-full rounded-lg sm:w-[320px]"><SelectValue placeholder="Select an installation" /></SelectTrigger>
              <SelectContent>{clients.map((client) => <SelectItem key={client.id} value={client.id}>{client.shopName} · {client.branchName || client.platform}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setActiveTab("installations")}><Cpu className="mr-1.5 h-3.5 w-3.5" />Directory</Button>
          </div>

          <section className="overflow-hidden rounded-2xl border border-[#0E5B52]/10 bg-white shadow-[0_22px_60px_-40px_rgba(6,78,59,.4)]">
            {selected ? (
              <SyncCockpit
                canOperateRemotely={canOperateRemotely}
                client={selected}
                commands={commands.filter((command) => command.clientId === selected.id)}
                incident={incident}
                onChanged={() => queryClient.invalidateQueries({ queryKey: ["admin-sync-overview"] })}
              />
            ) : (
              <div className="flex min-h-[360px] items-center justify-center p-8 text-center"><div><Wifi className="mx-auto h-8 w-8 text-[#0E5B52]/35" /><div className="mt-3 text-sm font-semibold text-[#064E3B]">Select an installation from the directory</div></div></div>
            )}
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WorkspaceTab({ icon: Icon, label, meta, value }) {
  return (
    <TabsTrigger
      value={value}
      className="group h-auto min-w-0 justify-start gap-2.5 rounded-xl px-3 py-2.5 text-left data-[state=active]:bg-[#0E5B52] data-[state=active]:text-white data-[state=active]:shadow-md sm:px-4"
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#0E5B52]/8 text-[#0E5B52] group-data-[state=active]:bg-white/10 group-data-[state=active]:text-white">
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[11px] font-semibold sm:text-xs">{label}</span>
        <span className="mt-0.5 hidden truncate text-[9px] font-normal opacity-65 sm:block">{meta}</span>
      </span>
    </TabsTrigger>
  );
}

function IncidentRail({ incidents, loading, onOpen, onViewAll }) {
  return (
    <section className="overflow-hidden rounded-xl border border-[#0E5B52]/8 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-[#0E5B52]/8 px-4 py-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Priority rail</div>
          <div className="mt-0.5 text-sm font-semibold text-[#064E3B]">Incidents requiring attention</div>
        </div>
        <Button variant="ghost" size="sm" onClick={onViewAll}>Browse installations <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Button>
      </div>
      {loading ? (
        <div className="grid gap-3 p-3 md:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, index) => <Skeleton className="h-28 rounded-xl" key={index} />)}</div>
      ) : incidents.length ? (
        <div className="grid gap-3 p-3 md:grid-cols-2 xl:grid-cols-4">
          {incidents.slice(0, 4).map((item) => (
            <button key={item.id} onClick={() => onOpen(item)} className="rounded-xl border border-[#0E5B52]/8 bg-[#FBFCFB] p-3 text-left transition hover:-translate-y-0.5 hover:border-[#0E5B52]/20 hover:shadow-md">
              <div className="flex items-center justify-between gap-2"><Badge variant="outline" className={`rounded-full text-[8px] ${healthTone(item.severity)}`}>{item.severity}</Badge><span className="text-[9px] text-[#9CA3AF]">{timeAgo(item.lastObservedAt)}</span></div>
              <div className="mt-2 truncate text-xs font-semibold text-[#064E3B]">{item.shopName}</div>
              <div className="mt-1 line-clamp-2 text-[10px] leading-4 text-[#6B7280]">{item.summary}</div>
            </button>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-3 p-5 text-xs text-[#6B7280]"><CheckCircle2 className="h-5 w-5 text-emerald-500" />No active incidents. The priority rail is clear.</div>
      )}
    </section>
  );
}

function QueueTrend({ data, loading }) {
  return (
    <div className="rounded-xl border border-[#0E5B52]/8 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Queue pressure · 24 hours</div>
          <div className="mt-1 text-sm font-semibold text-[#064E3B]">Waiting versus permanently failed</div>
        </div>
        <div className="flex gap-3 text-[10px] text-[#6B7280]"><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-sky-400" />Queued</span><span><i className="mr-1 inline-block h-2 w-2 rounded-full bg-rose-400" />Failed</span></div>
      </div>
      <div className="mt-3 h-64 lg:h-72">
        {loading ? <Skeleton className="h-full w-full rounded-xl" /> : data.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="queued-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#38BDF8" stopOpacity={0.34} /><stop offset="100%" stopColor="#38BDF8" stopOpacity={0.02} /></linearGradient>
                <linearGradient id="failed-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FB7185" stopOpacity={0.3} /><stop offset="100%" stopColor="#FB7185" stopOpacity={0.02} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8EFEB" />
              <XAxis dataKey="capturedAt" tickFormatter={(value) => new Date(value).toLocaleTimeString([], { hour: "2-digit" })} tick={{ fontSize: 9, fill: "#6B7280" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: "#6B7280" }} axisLine={false} tickLine={false} width={28} />
              <Tooltip labelFormatter={(value) => new Date(value).toLocaleString()} contentStyle={{ borderRadius: 12, border: "1px solid #DDE8E3", fontSize: 11 }} />
              <Area type="monotone" dataKey="queued" stroke="#0EA5E9" fill="url(#queued-fill)" strokeWidth={2} />
              <Area type="monotone" dataKey="failed" stroke="#F43F5E" fill="url(#failed-fill)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        ) : <div className="flex h-full items-center justify-center rounded-xl bg-[#F7FAF8] text-xs text-[#6B7280]">Trend appears after telemetry begins.</div>}
      </div>
    </div>
  );
}

function ReliabilityBrief({ metrics }) {
  const total = metrics.reportingClients || 0;
  const atRisk = (metrics.criticalClients || 0) + (metrics.degradedClients || 0) + (metrics.offlineClients || 0);
  const score = total ? Math.max(0, Math.round(((total - atRisk) / total) * 100)) : 100;
  return (
    <div className="relative overflow-hidden rounded-xl border border-[#0E5B52]/8 bg-[#071E1A] p-5 text-white shadow-sm">
      <div className="absolute -right-10 -top-12 h-40 w-40 rounded-full bg-emerald-400/10 blur-2xl" />
      <div className="relative">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/55"><Gauge className="h-4 w-4 text-emerald-300" /> Reliability posture</div>
        <div className="mt-4 flex items-end gap-2"><span className="text-4xl font-semibold">{score}</span><span className="pb-1 text-sm text-white/50">/100</span></div>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-300" style={{ width: `${score}%` }} /></div>
        <div className="mt-5 grid grid-cols-2 gap-2 text-[10px]">
          <div className="rounded-lg bg-white/[0.06] p-2.5"><div className="text-white/45">Offline</div><div className="mt-1 text-base font-semibold">{metrics.offlineClients || 0}</div></div>
          <div className="rounded-lg bg-white/[0.06] p-2.5"><div className="text-white/45">Oldest queue</div><div className="mt-1 text-base font-semibold">{formatDuration(metrics.oldestQueueAgeSeconds || 0)}</div></div>
        </div>
      </div>
    </div>
  );
}

function InstallationCard({ client, incident, onClick }) {
  const PlatformIcon = client.platform === "web" ? Laptop2 : Smartphone;
  return (
    <button onClick={onClick} className="w-full rounded-xl border border-[#0E5B52]/8 bg-white px-4 py-4 text-left transition hover:-translate-y-0.5 hover:border-[#0E5B52]/20 hover:bg-[#F7FAF8] hover:shadow-md">
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ${healthDot(client.healthStatus)}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3"><span className="truncate text-sm font-semibold text-[#064E3B]">{client.shopName}</span><span className="text-[9px] text-[#9CA3AF]">{timeAgo(client.lastHeartbeatAt)}</span></div>
          <div className="mt-1 flex items-center gap-1.5 truncate text-[11px] text-[#6B7280]"><PlatformIcon className="h-3 w-3" />{client.branchName || "Shop-wide"} · {client.deviceName || client.platform}</div>
          <div className="mt-3 flex items-center justify-between gap-2">
            <Badge variant="outline" className={`rounded-full text-[9px] ${healthTone(client.healthStatus)}`}>{client.healthStatus}</Badge>
            <span className="text-[10px] font-medium text-[#6B7280]">{client.queuedCount} queued · {client.failedCount} failed</span>
          </div>
          {incident && <div className="mt-2 truncate text-[10px] text-rose-700">{incident.summary}</div>}
          <div className="mt-3 flex items-center justify-end text-[10px] font-semibold text-[#0E5B52]">Open Case Desk <ArrowUpRight className="ml-1 h-3 w-3" /></div>
        </div>
      </div>
    </button>
  );
}

function SyncCockpit({ canOperateRemotely, client, commands, incident, onChanged }) {
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const commandMutation = useMutation({
    mutationFn: (commandType) => requestAdminSyncCommand(client.id, { commandType, reason: reason.trim() }),
    onSuccess: () => {
      toast.success("Command requested. It will run when the installation checks in.");
      setReason("");
      onChanged();
    },
    onError: (error) => toast.error(error.message || "Command could not be requested"),
  });
  const incidentMutation = useMutation({
    mutationFn: ({ action, resolutionNote = "" }) => updateAdminSyncIncident(incident.id, { action, resolutionNote, version: incident.version }),
    onSuccess: () => { toast.success("Incident updated"); onChanged(); },
    onError: (error) => toast.error(error.message || "Incident could not be updated"),
  });
  const supportMutation = useMutation({
    mutationFn: () => createAdminSyncSupportCase(incident.id),
    onSuccess: (payload) => {
      toast.success("Linked support case created");
      onChanged();
      if (payload.incident?.supportTicketId) navigate("/support");
    },
    onError: (error) => toast.error(error.message || "Support case could not be created"),
  });
  const operationEntries = Object.entries(client.operationCounts || {}).sort((a, b) => b[1] - a[1]);
  const totalOperations = Math.max(1, operationEntries.reduce((total, [, count]) => total + count, 0));
  const canCommand = canOperateRemotely && reason.trim().length >= 3 && !commandMutation.isPending;

  return (
    <div className="min-h-[650px]">
      <header className="border-b border-[#0E5B52]/8 bg-[radial-gradient(circle_at_top_right,#E5F6EE,transparent_45%)] px-5 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2"><Badge variant="outline" className={`rounded-full ${healthTone(client.healthStatus)}`}>{client.healthStatus}</Badge><span className="text-[10px] font-semibold uppercase tracking-wider text-[#6B7280]">{client.platform} · schema {client.schemaVersion}</span></div>
            <h2 className="mt-2 text-xl font-semibold tracking-[-0.025em] text-[#064E3B]">{client.shopName}</h2>
            <div className="mt-1 text-xs text-[#6B7280]">{client.branchName || "Shop-wide"} · {client.deviceName || "Unnamed installation"} · app {client.appVersion || "unknown"}</div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate(`/shops/${client.shopId}`)}>Shop profile <ArrowUpRight className="ml-1.5 h-3.5 w-3.5" /></Button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-2 xl:grid-cols-4">
          <MiniMetric icon={CloudCog} label="Queue" value={client.queuedCount} />
          <MiniMetric icon={AlertOctagon} label="Failed" value={client.failedCount} danger={client.failedCount > 0} />
          <MiniMetric icon={Clock3} label="Queue age" value={queueAge(client)} />
          <MiniMetric icon={Radio} label="Last heartbeat" value={timeAgo(client.lastHeartbeatAt)} />
        </div>
      </header>

      <div className="grid gap-0 2xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="space-y-5 p-5">
          {incident ? (
            <div className={`rounded-xl border p-4 ${incident.severity === "critical" ? "border-rose-200 bg-rose-50/70" : "border-orange-200 bg-orange-50/70"}`}>
              <div className="flex items-start gap-3">
                <AlertOctagon className={`mt-0.5 h-5 w-5 ${incident.severity === "critical" ? "text-rose-600" : "text-orange-600"}`} />
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-[#263B35]">{incident.summary}</div><div className="mt-1 text-[11px] text-[#6B7280]">{incident.reasonCode} · observed {timeAgo(incident.firstObservedAt)}</div></div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {incident.status === "open" && <Button size="sm" variant="outline" onClick={() => incidentMutation.mutate({ action: "claim" })} disabled={incidentMutation.isPending}><UserCheck className="mr-1.5 h-3.5 w-3.5" />Claim incident</Button>}
                {!incident.supportTicketId ? <Button size="sm" variant="outline" onClick={() => supportMutation.mutate()} disabled={supportMutation.isPending}><FilePlus2 className="mr-1.5 h-3.5 w-3.5" />Create support case</Button> : <Button size="sm" variant="outline" onClick={() => navigate("/support")}><LifeBuoy className="mr-1.5 h-3.5 w-3.5" />{incident.supportReference}</Button>}
                <Button size="sm" variant="ghost" onClick={() => incidentMutation.mutate({ action: "resolve", resolutionNote: reason.trim() })} disabled={incidentMutation.isPending || reason.trim().length < 3}><CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />Resolve with reason</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4"><ShieldCheck className="h-5 w-5 text-emerald-700" /><div><div className="text-sm font-semibold text-emerald-900">No active sync incident</div><div className="mt-0.5 text-[11px] text-emerald-800/70">This installation remains under passive health monitoring.</div></div></div>
          )}

          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Queue composition</div>
            <div className="mt-3 space-y-3">
              {operationEntries.length ? operationEntries.map(([operation, count]) => (
                <div key={operation}>
                  <div className="mb-1.5 flex items-center justify-between text-[11px]"><span className="font-medium text-[#33443F]">{operation.replaceAll("_", " ")}</span><span className="text-[#6B7280]">{count}</span></div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-[#EDF3F0]"><div className="h-full rounded-full bg-gradient-to-r from-[#0E5B52] to-[#34D399]" style={{ width: `${Math.max(6, (count / totalOperations) * 100)}%` }} /></div>
                </div>
              )) : <div className="rounded-xl bg-[#F7FAF8] p-4 text-xs text-[#6B7280]">No operations are waiting on this installation.</div>}
            </div>
          </div>

          <div className="rounded-xl border border-[#0E5B52]/8 bg-[#FBFCFB] p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-[#064E3B]"><Cpu className="h-4 w-4" />Safe diagnostics</div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
              <Diagnostic label="Worker" value={client.workerState} />
              <Diagnostic label="Last success" value={client.lastSuccessAt ? timeAgo(client.lastSuccessAt) : "Never reported"} />
              <Diagnostic label="Last attempt" value={client.lastAttemptAt ? timeAgo(client.lastAttemptAt) : "Never reported"} />
              <Diagnostic label="Error code" value={client.lastErrorCode || "None"} danger={Boolean(client.lastErrorCode)} />
            </div>
            <div className="mt-3 flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[10px] text-[#6B7280] ring-1 ring-[#0E5B52]/7"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />No customer, sale, debt or product payloads are collected.</div>
          </div>
        </div>

        <aside className="border-t border-[#0E5B52]/8 bg-[#F7FAF8] p-4 2xl:border-l 2xl:border-t-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Remote assistance</div>
          <div className="mt-2 text-sm font-semibold text-[#064E3B]">Request a safe command</div>
          <p className="mt-1 text-[11px] leading-5 text-[#6B7280]">Commands are delivered asynchronously when this installation is online. Every request is expiring and audited.</p>
          {!canOperateRemotely && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[10px] leading-4 text-amber-900">
              Remote commands require sync-management permission. You can still inspect telemetry and coordinate support.
            </div>
          )}
          <Textarea disabled={!canOperateRemotely} value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={500} placeholder="Operational reason (required)…" className="mt-4 resize-none bg-white text-xs" />
          <div className="mt-3 space-y-2">
            <CommandButton icon={TimerReset} title="Retry due operations" description="Requeue recoverable failures only" disabled={!canCommand} onClick={() => commandMutation.mutate("retry_due_operations")} />
            <CommandButton icon={RefreshCw} title="Refresh workspace" description="Pull the latest server snapshot" disabled={!canCommand} onClick={() => commandMutation.mutate("refresh_workspace")} />
            <CommandButton icon={Activity} title="Request health snapshot" description="Refresh diagnostics without payloads" disabled={!canCommand} onClick={() => commandMutation.mutate("upload_health_snapshot")} />
          </div>
          <div className="mt-6 border-t border-[#0E5B52]/8 pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#6B7280]">Command trail</div>
            <div className="mt-3 space-y-2">
              {commands.length ? commands.slice(0, 4).map((command) => (
                <div key={command.id} className="rounded-lg border border-[#0E5B52]/8 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[10px] font-semibold text-[#33443F]">{command.commandType.replaceAll("_", " ")}</span>
                    <Badge variant="outline" className={`rounded-full text-[8px] ${command.status === "completed" ? healthTone("healthy") : command.status === "failed" || command.status === "expired" ? healthTone("critical") : healthTone("watching")}`}>{command.status}</Badge>
                  </div>
                  <div className="mt-1 text-[9px] text-[#9CA3AF]">{timeAgo(command.requestedAt)} · expires {timeAgo(command.expiresAt)}</div>
                </div>
              )) : <div className="rounded-lg bg-white p-3 text-[10px] text-[#6B7280] ring-1 ring-[#0E5B52]/7">No commands requested for this installation.</div>}
            </div>
          </div>
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[10px] leading-4 text-amber-900"><strong>Honest delivery:</strong> offline installations cannot be forced. The command remains requested until the client checks in or it expires.</div>
        </aside>
      </div>
    </div>
  );
}

function MiniMetric({ danger = false, icon: Icon, label, value }) {
  return <div className="rounded-xl border border-[#0E5B52]/8 bg-white/85 p-3"><div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-[#6B7280]"><Icon className="h-3.5 w-3.5" />{label}</div><div className={`mt-2 truncate text-sm font-semibold ${danger ? "text-rose-700" : "text-[#064E3B]"}`}>{value}</div></div>;
}

function Diagnostic({ danger = false, label, value }) {
  return <div className="rounded-lg bg-white p-3 ring-1 ring-[#0E5B52]/7"><div className="text-[9px] uppercase tracking-wider text-[#9CA3AF]">{label}</div><div className={`mt-1 truncate font-medium ${danger ? "text-rose-700" : "text-[#33443F]"}`}>{value}</div></div>;
}

function CommandButton({ description, disabled, icon: Icon, onClick, title }) {
  return <button disabled={disabled} onClick={onClick} className="flex w-full items-center gap-3 rounded-xl border border-[#0E5B52]/8 bg-white p-3 text-left transition hover:border-[#0E5B52]/25 hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-45"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#0E5B52]/8 text-[#0E5B52]"><Icon className="h-4 w-4" /></span><span><span className="block text-xs font-semibold text-[#064E3B]">{title}</span><span className="mt-0.5 block text-[10px] text-[#6B7280]">{description}</span></span></button>;
}

function ClientListSkeleton() {
  return <div className="space-y-1 p-3">{Array.from({ length: 6 }).map((_, index) => <Skeleton className="h-24 w-full rounded-xl" key={index} />)}</div>;
}

function EmptyState({ action, body, title }) {
  return <div className="p-8 text-center"><CloudOff className="mx-auto h-7 w-7 text-[#0E5B52]/30" /><div className="mt-3 text-sm font-semibold text-[#064E3B]">{title}</div><div className="mt-1 text-xs leading-5 text-[#6B7280]">{body}</div>{action && <Button size="sm" variant="outline" className="mt-4" onClick={action}>Try again</Button>}</div>;
}
